// src/pages/Report.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getApiBaseUrl } from '../config/api';
import { fetchWithAuth } from '../utils/auth';
import { refusalText } from './live-review/account-reasons';

interface ReportDetails {
  report: Record<string, any>;
  itemDetails: Record<string, any>;
}

interface ReportRecord {
  _id: string;
  [key: string]: any;
}

const ReportPage: React.FC = () => {
  const { reportId, itemType } = useParams<{ reportId: string; itemType: string }>();
  const navigate = useNavigate();

  const [details, setDetails] = useState<ReportDetails | null>(null);
  const [allReports, setAllReports] = useState<ReportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);

  const [selectedReporterId, setSelectedReporterId] = useState<string | null>(null);
  const [reportsByUser, setReportsByUser] = useState<ReportRecord[]>([]);
  const [loadingByUser, setLoadingByUser] = useState(false);
  const [actionLoading, setActionLoading] = useState<'DELETE' | 'BLOCK' | null>(null);

  const handleAction = async (action: 'DELETE' | 'BLOCK') => {
    if (!reportId || !itemType) return;
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return;
    }
    setActionLoading(action);
    try {
      const res = await fetchWithAuth(`${getApiBaseUrl()}/video/t/reportAction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ReportID: reportId,
          ItemType: itemType,
          Action: action,
        }),
      });
      const json = await res.json();
      if (res.ok) {
        toast.success(json.message || `${action} succeeded`);
        navigate(-1);
      } else {
        toast.error(refusalText(res.status, json.message, `${action} failed`));
      }
    } catch {
      toast.error(`Network error during ${action.toLowerCase()}`);
    } finally {
      setActionLoading(null);
    }
  };

  // derive the reporter’s display name
  const selectedReporterName = useMemo(() => {
    if (!selectedReporterId) return '';
    const reporter = allReports.find((r) => r.ReporterUserID === selectedReporterId);
    return reporter ? reporter.ReporterUserName : selectedReporterId;
  }, [allReports, selectedReporterId]);

  // ——————————————
  // SUMMARY COLUMNS:
  const summaryColumns = [
    { key: 'UserID', label: 'User ID' },
    { key: 'Report', label: 'Report' },
    { key: 'ReporterUserID', label: 'Reporter User ID' },
    { key: 'ReporterUserName', label: 'Reporter User Name' },
    { key: 'UserName', label: 'User Name' },
  ];

  // ——————————————
  // INLINE “BY THIS USER” COLUMNS:
  const userColumns = [
    { key: '_id', label: 'ReportID' },
    { key: 'Reviewed', label: 'Reviewed' },
    { key: 'ItemType', label: 'Item Type' },
    { key: 'Report', label: 'Report', isWide: true },
    { key: 'ReporterUserID', label: 'Reporter User ID', isWide: true },
    { key: 'ItemID', label: 'Item ID' },
    { key: 'CommentID', label: 'Comment ID' },
    { key: 'UserID', label: 'User ID' },
  ];

  // helper to humanize field names
  const humanize = (s: string) =>
    s
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();

  // fetch main details + summary list
  useEffect(() => {
    if (!reportId || !itemType) return;
    const raw = localStorage.getItem('authToken');
    if (!raw) {
      toast.error('No token found. Please log in.');
      navigate(-1);
      return;
    }
    const token = raw;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${getApiBaseUrl()}/video/t/findReport`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ ReportID: reportId, ItemType: itemType }),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.message);
        setDetails(j.data);
      } catch (e: any) {
        toast.error(e.message);
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    const fetchAll = async () => {
      setLoadingAll(true);
      try {
        const r = await fetch(`${getApiBaseUrl()}/video/t/findAllReports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            FetchDetails: true,
            ReportID: reportId,
            ItemType: itemType,
          }),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.message);
        setAllReports(j.data || []);
      } catch (e: any) {
        toast.error(e.message);
      } finally {
        setLoadingAll(false);
      }
    };

    fetchDetails();
    fetchAll();
  }, [reportId, itemType, navigate]);

  // load full reports by a particular reporter
  const loadReportsByUser = async (reporterId: string) => {
    setSelectedReporterId(reporterId);
    setLoadingByUser(true);
    setReportsByUser([]);
    const token = localStorage.getItem('authToken')!;
    try {
      const r = await fetch(`${getApiBaseUrl()}/video/t/findAllReports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          ReporterUserID: true,
          ReportID: reporterId,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.message);
      setReportsByUser(j.data || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoadingByUser(false);
    }
  };

  if (loading) return <p className='text-center mt-10'>Loading report…</p>;
  if (!details) return <p className='text-center mt-10'>No details available</p>;

  const { itemDetails } = details;
  const videoUrl = itemType === 'VIDEO' ? itemDetails.VideoUrl : undefined;
  const detailKeys = Object.keys(itemDetails);

  return (
    <>
      <Navbar />
      <div className='min-h-screen bg-gray-100 p-6'>
        <button onClick={() => navigate(-1)} className='mb-4 text-blue-600 hover:underline'>
          &larr; Back to Reports
        </button>

        <div className='bg-white rounded-lg shadow-lg max-w-8xl mx-auto p-6 flex flex-col md:flex-row gap-6'>
          {/* LEFT: media + full itemDetails */}
          <div className='md:w-1/3 space-y-6 overflow-y-auto max-h-[80vh]'>
            <div className='flex items-center space-x-2'>
              <button
                onClick={() => handleAction('DELETE')}
                disabled={actionLoading === 'DELETE'}
                className='bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md disabled:opacity-50'
              >
                {actionLoading === 'DELETE' ? 'Deleting…' : 'Delete'}
              </button>
              <button
                onClick={() => handleAction('BLOCK')}
                disabled={actionLoading === 'BLOCK'}
                className='bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded-md disabled:opacity-50'
              >
                {actionLoading === 'BLOCK' ? 'Blocking…' : 'Block'}
              </button>
            </div>
            {videoUrl && <video controls autoPlay loop className='w-full rounded-lg object-cover' src={`https://media.tezda.com${videoUrl}`} />}

            {/* Video Status Reason - Most Important */}
            {itemDetails.VideoStatusReason && (
              <div className='bg-amber-50 border border-amber-300 rounded-lg p-4'>
                <h4 className='text-md font-semibold text-amber-800 mb-2 flex items-center gap-2'>
                  <span>⚠️</span> Video Status Reason
                </h4>
                <p className='text-amber-900 font-medium'>{itemDetails.VideoStatusReason}</p>
              </div>
            )}

            <section>
              <h3 className='text-xl font-bold mb-2'>{itemType} Details</h3>
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Field</th>
                      <th className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>Value</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {detailKeys
                      .filter((k) => k !== 'VideoStatusReason')
                      .map((k) => (
                        <tr key={k}>
                          <td className='px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-700'>{humanize(k)}</td>
                          <td className='px-4 py-2 whitespace-nowrap text-sm text-gray-700'>
                            {k.toLowerCase().includes('date') || k === 'createdAt' ? new Date(itemDetails[k]).toLocaleString() : String(itemDetails[k])}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          {/* RIGHT: summary table + inline details */}
          <div className='md:w-2/3 space-y-6 overflow-y-auto max-h-[80vh]'>
            <section>
              <h3 className='text-xl font-bold mb-2'>Total Reports Against This {itemType}</h3>

              {loadingAll ? (
                <p>Loading all reports…</p>
              ) : !allReports.length ? (
                <p className='text-gray-500'>No reports found.</p>
              ) : (
                <div className='overflow-x-auto'>
                  <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                      <tr>
                        {summaryColumns.map((col) => (
                          <th key={col.key} className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                      {allReports.map((r) => (
                        <tr key={r._id} className='hover:bg-gray-100 cursor-pointer' onClick={() => loadReportsByUser(r.ReporterUserID)}>
                          {summaryColumns.map((col) => (
                            <td key={col.key} className='px-4 py-2 whitespace-nowrap text-sm text-gray-700'>
                              {String(r[col.key] ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {selectedReporterId && (
              <section>
                <h3 className='text-xl font-bold mb-2'>All Reports By User {selectedReporterName}</h3>

                {loadingByUser ? (
                  <p>Loading…</p>
                ) : !reportsByUser.length ? (
                  <p className='text-gray-500'>No reports by this user.</p>
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='min-w-full divide-y divide-gray-200'>
                      <thead className='bg-gray-50'>
                        <tr>
                          {userColumns.map((col) => (
                            <th key={col.key} className='px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className='bg-white divide-y divide-gray-200'>
                        {reportsByUser.map((r) => (
                          <tr key={r._id}>
                            {userColumns.map((col) => {
                              let val = (r as any)[col.key];
                              if (col.key === 'ReviewedAt') {
                                val = val ? new Date(val).toLocaleString() : '';
                              }
                              return (
                                <td key={col.key} className='px-4 py-2 text-sm text-gray-700 whitespace-nowrap'>
                                  {col.isWide ? (
                                    <div className='overflow-x-auto whitespace-nowrap'>{String(val ?? '')}</div>
                                  ) : (
                                    <div className='truncate'>{String(val ?? '')}</div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportPage;
